import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { delTunnel, uploadImageTunnel, videoErrorTunnel, publishSuccessTunnel } from "../constants/tunnels";
import PublishSuccessModal from "../Components/Scene/PublishSuccessModal";
import ExitEditorModal from "../Components/Scene/ExitEditorModal";
import {
  Button,
  GridItem,
  Grid,
  IconButton,
  Box,
  Icon,
  AlertRoot,
  AlertContent,
  Spinner,
  Text,
  HStack,
} from "@chakra-ui/react";
import ErrorBoundary, { useErrorHandler } from "../Components/ErrorBoundary";
import {
  VscHome,
  VscEye,
  VscEdit,
  VscChevronLeft,
  VscChevronRight,
} from "react-icons/vsc";
import { useUser, UserButton, useAuth, useClerk } from "@clerk/clerk-react";

import MainScene from "../Components/MainScene";
import ObjectPanel from "../Components/ObjectPanel";
import useUndoRedoShortcuts from "../Components/HIstoryComponent";
import PointerLockUI from "../Components/Overlay/PointerLockUI";
import {
  useModelStore,
  useUserInterfaceStore,
  useRoomStore,
  usePlacedModelsStore,
  useSessionStore,
  useSafariBarStore,
} from "../store";
import { useApiStore } from "../store/apiStore";
import ToolbarPanel from "../Components/ObjectPanel/ToolbarPanel";
import { roomApi, assetsApi } from "../services/api";
import { assetLoader } from "../store/apiStore";
import debounce from "lodash.debounce";
import { resetCameraControls } from "../constants/Helpers";
import useIsMobile from "../Components/Hooks/useIsMobile";
import TutorialButton from "../Components/ObjectPanel/TutorialButton";
import MobileTransformPanel from "../Components/ObjectPanel/MobileTransformPanel";
import TransformTools from "../Components/ObjectPanel/TransformTools";
import { getPlayerUrl } from "../utils/roomNavigation";
import { track, Events } from "../utils/analytics";
import { createDefaultRoomConfig, DEFAULT_ROOM_CONFIG, applyModelDefaults } from "../constants/defaults";
import { ROUTES } from "../constants/routes";

// Step progress SVGs removed - no longer showing step indicator
// import { ReactComponent as Step1Svg } from "../Icons/progressSvgStep1.svg";
// import { ReactComponent as Step2Svg } from "../Icons/progressSvgStep2.svg";
// import { ReactComponent as Step3Svg } from "../Icons/progressSvgStep3.svg";

import TutorialOverlay from "../Components/ObjectPanel/TutorialOverlay";
// import StepIndicator from "../Components/ObjectPanel/StepIndicator";
import { publishingApi } from "../services/publishingApi";

import GuestSessionWarning from "../Components/GuestSessionWarning";
import { captureAndSaveRoomThumbnail, SCREENSHOT_VERSION } from "../utils/roomScreenshot";
import { getEditorRenderer } from "../utils/rendererRegistry";

// Module-level tracking: survives component remounts during step navigation.
// When users click "Next Step" the EditorPage unmounts and a fresh instance mounts,
// resetting all useRef values. These globals let the new instance know it's still
// editing the same room and should skip the backend reload + store reset.
let _globalEditingRoomId = null;
// Room metadata that must survive step-navigation remounts (not stored in zustand)
let _globalRoomName = '';
let _globalRoomSlug = null;
let _globalRoomTags = [];
let _globalListedInDirectory = true;
// Called by video upload components to force an immediate backend save so the
// video URL is durable before the user navigates to the next step or the player.
let _globalFlushSave = null;
export function flushEditorSave() {
  if (_globalFlushSave) _globalFlushSave();
}

function triggerAutoScreenshotCapture(roomId, user, decorations) {
  const renderer = getEditorRenderer();
  if (!renderer || !roomId) {
    return;
  }
  
  const userInfo = user ? { isSignedIn: true, user: { id: user.id } } : null;
  
  captureAndSaveRoomThumbnail(renderer, roomId, userInfo, {
    silent: true,
    decorations: decorations || []
  }).catch(() => {
    // Silently ignore errors - screenshot is not critical
  });
}

function EditorPageContent({ panels, keyForPanel = "basic" }) {
  const params = useParams();
  const navigate = useNavigate();
  const { isSignedIn, user, isLoaded } = useUser();
  const { getToken } = useAuth();
  const { redirectToSignUp } = useClerk();
  const handleAsyncError = useErrorHandler();

  // Support both legacy roomId and new slug-based URLs
  const [roomId, setRoomId] = useState(null);
  const [isLoadingRoomId, setIsLoadingRoomId] = useState(true);
  // Store room metadata for slug-based navigation
  const [roomMeta, setRoomMeta] = useState({ slug: null });

  const isMobile = useIsMobile();

  // Turn on the dark iOS 26 Safari bar tint while the editor is open, and keep the
  // page background dark so it never flashes light behind the editor. Driven from
  // the page (not a URL check in App.js) so it covers every /editor/* step.
  const setSafariBars = useSafariBarStore((s) => s.setActive);
  useEffect(() => {
    setSafariBars(true);
    document.documentElement.style.backgroundColor = '#000';
    document.body.style.backgroundColor = '#000';
    return () => {
      setSafariBars(false);
      document.documentElement.style.backgroundColor = '';
      document.body.style.backgroundColor = '';
    };
  }, [setSafariBars]);

  // Prevent page scroll on mobile - only the 3D canvas and sidebar should handle touch
  useEffect(() => {
    if (isMobile) {
      // Lock body scroll
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      document.documentElement.style.overflow = 'hidden';

      // Prevent touchmove on document level, but allow it on scrollable elements
      const preventScroll = (e) => {
        // Allow scroll inside elements with overflow scroll/auto
        let target = e.target;
        while (target && target !== document.body) {
          const style = window.getComputedStyle(target);
          if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
            return; // Allow scroll in scrollable containers
          }
          target = target.parentElement;
        }
        e.preventDefault();
      };

      document.addEventListener('touchmove', preventScroll, { passive: false });

      return () => {
        // Restore on unmount
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.height = '';
        document.documentElement.style.overflow = '';
        document.removeEventListener('touchmove', preventScroll);
      };
    }
  }, [isMobile]);

  // Note: Not using memoized error handler in effects to avoid dependency instability
  // The handleAsyncError is only used in catch blocks outside of effects

  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  // Publishing state
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [showPublishSuccess, setShowPublishSuccess] = useState(false);
  const [publishedUrl, setPublishedUrl] = useState(null);
  // Whether this room has ever been published. Drives the exit confirmation:
  // the "not published yet" reminder only makes sense for unpublished rooms.
  const [isPublished, setIsPublished] = useState(false);

  // Screenshot capture state (currently unused while manual capture UI is commented out)
  // eslint-disable-next-line no-unused-vars
  const [_isCapturingScreenshot, setIsCapturingScreenshot] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [screenshotError, setScreenshotError] = useState(null);

  // Preview navigation state
  const [isNavigatingToPreview, setIsNavigatingToPreview] = useState(false);


  // Tutorial state - reset when keyForPanel changes
  const [showTutorial, setShowTutorial] = useState(false);

  // Reset tutorial state when keyForPanel changes
  // Shows tutorial on first time through the editor (per browser/device)
  useEffect(() => {
    const tutorialKey = `tutorial_completed_${keyForPanel}`;
    const isCompleted = localStorage.getItem(tutorialKey) === "true";
    const shouldShow = !isCompleted;

    setShowTutorial(shouldShow);
  }, [keyForPanel]); // Re-run when keyForPanel changes

  // Set room lining visibility based on current step
  useEffect(() => {
    // Show room lining in all steps except step 3 (furniture placement)
    const shouldShowLining = keyForPanel !== "step3";
    useUserInterfaceStore.getState().setShowRoomLining(shouldShowLining);
  }, [keyForPanel]);

  // Set auto-rotate camera for step 4 (publish step)
  useEffect(() => {
    const shouldAutoRotate = keyForPanel === "step4";
    useUserInterfaceStore.getState().setAutoRotateCamera(shouldAutoRotate);
    // Cleanup: disable auto-rotate when leaving step 4
    return () => {
      useUserInterfaceStore.getState().setAutoRotateCamera(false);
    };
  }, [keyForPanel]);

  // Persist current editor route so player/sign-in can return to the same step
  useEffect(() => {
    sessionStorage.setItem('lastEditorRoute', window.location.pathname);
  }, [keyForPanel, roomId]);

  useUndoRedoShortcuts();

  const editingRoom = useUserInterfaceStore((s) => s.editingRoom);
  const mobileEditMode = useUserInterfaceStore((s) => s.mobileEditMode);
  const setMobileEditMode = useUserInterfaceStore((s) => s.setMobileEditMode);
  const showDesktopMovePanel = useUserInterfaceStore((s) => s.showDesktopMovePanel);
  const selectedMeshes = useModelStore((s) => s.selectedMeshes);
  const cameraControls = useModelStore((s) => s.cameraControls);

  // Reset mobileEditMode to "assets" when leaving step 3
  useEffect(() => {
    if (keyForPanel !== "step3") {
      setMobileEditMode("assets");
    }
  }, [keyForPanel, setMobileEditMode]);

  // Mobile step-3 toggle between placing objects (assets) and moving them (transform)
  const renderMobileModeToggle = () => {
    const isDisabled = mobileEditMode === "assets" && selectedMeshes.length === 0;
    return (
      <Button
        w="100%"
        size="md"
        fontFamily="DM Mono"
        fontWeight="500"
        borderRadius="0"
        color={isDisabled ? "gray.400" : "black"}
        bg={isDisabled ? "gray.600" : "#26EF96"}
        opacity={isDisabled ? 0.5 : 1}
        pointerEvents={isDisabled ? "none" : "auto"}
        _hover={{ opacity: 0.9 }}
        onClick={() => {
          setMobileEditMode(mobileEditMode === "assets" ? "transform" : "assets");
        }}
      >
        {mobileEditMode === "transform" ? "Add More Objects" : "Move Object"}
      </Button>
    );
  };
  const lockCameraForEditor = useUserInterfaceStore((s) => s.lockCameraForEditor);
  // const isPanelExpanded = useUserInterfaceStore((s) => s.isPanelExpanded);

  // Store getters for saving - use individual selectors to avoid creating new objects
  const scaleX = useRoomStore((s) => s.scaleX);
  const scaleZ = useRoomStore((s) => s.scaleZ);
  const height = useRoomStore((s) => s.height);
  const materialType = useRoomStore((s) => s.materialType);
  const materialId = useRoomStore((s) => s.materialId);
  const materialOptions = useRoomStore((s) => s.materialOptions);
  const decorations = useRoomStore((s) => s.decorations);
  const lightIntensity = useRoomStore((s) => s.lightIntensity);
  const roomLiningVisible = useRoomStore((s) => s.roomLiningVisible);
  const shaderOptions = useRoomStore((s) => s.shaderOptions);

  // Migration flag — true while guest rooms are being transferred to the new account
  const isMigrating = useSessionStore((state) => state.isMigrating);

  // Escape hatch: if the user navigated back to the editor without completing auth,
  // isMigrating stays true forever (until refresh). Clear it so the room can still load.
  useEffect(() => {
    if (isMigrating && !isSignedIn && isLoaded) {
      useSessionStore.getState().setIsMigrating(false);
    }
  }, [isMigrating, isSignedIn, isLoaded]);

  // Room name state (loaded from backend, not stored in zustand)
  const [roomName, setRoomName] = useState('');
  const [roomTags, setRoomTags] = useState([]);
  const [listedInDirectory, setListedInDirectory] = useState(true);
  const [hasPassword, setHasPassword] = useState(false);

  // Guest session expiration time (loaded from backend for guest users)
  const [roomExpiresAt, setRoomExpiresAt] = useState(null);

  // Memoize roomConfig to prevent unnecessary re-renders
  const roomConfig = useMemo(
    () => ({
      scaleX,
      scaleZ,
      height,
      materialType,
      materialId,
      materialOptions,
      decorations,
      lightIntensity,
      roomLiningVisible,
      shaderOptions,
    }),
    [scaleX, scaleZ, height, materialType, materialId, materialOptions, decorations, lightIntensity, roomLiningVisible, shaderOptions]
  );

  const placedModels = usePlacedModelsStore((s) => s.placedModels);

  // Track the last loaded roomId to detect changes
  const lastLoadedRoomId = useRef(null);
  const isInitializing = useRef(false);
  // Track if room data has been successfully loaded - prevents auto-save with empty data
  const roomDataReady = useRef(false);

  // Resolve roomId from URL params (either legacy roomId or slug-based)
  useEffect(() => {
    const resolveRoomId = async () => {
      // Reset editing state when resolving a new room
      isUserEditing.current = false;

      const identifier = params.roomIdentifier || params.roomId || params.roomSlug;

      if (!identifier) {
        setError('No room identifier provided');
        setIsLoadingRoomId(false);
        return;
      }

      // Detect if identifier is a room ID (format: room_XXXXX) or a slug
      const isRoomId = identifier.startsWith('room_');

      if (isRoomId) {
        // Direct room ID format
        console.log('Using room ID from URL:', identifier);
        setRoomId(identifier);
        setIsLoadingRoomId(false);
      } else {
        // Slug format - need to resolve to room ID
        console.log('Resolving roomId from slug:', identifier);
        try {
          const roomData = await roomApi.getRoomBySlug(identifier, { isSignedIn, user });
          setRoomId(roomData.id);
          setIsLoadingRoomId(false);
        } catch (error) {
          console.error('Failed to resolve room from slug:', error);
          setError('Room not found');
          setIsLoadingRoomId(false);
        }
      }
    };

    resolveRoomId();
  }, [params.roomIdentifier, params.roomId, params.roomSlug, isSignedIn, user]);

  // Load room data on mount - ONLY when roomId changes
  useEffect(() => {
    // Wait for roomId to be resolved
    if (isLoadingRoomId) return;

    // Wait for guest-room migration to complete after sign-in/sign-up
    // (migration runs async in authWrapper; loading too early causes a 403)
    if (isMigrating) return;

    // Prevent running effect if we're already initializing
    if (isInitializing.current) return;

    const loadRoom = async () => {
      if (!roomId) {
        setError("No room ID provided");
        setIsLoading(false);
        return;
      }

      // Snapshot avatar data from localStorage before any clears wipe the store
      const preLoadModels = usePlacedModelsStore.getState().placedModels;
      const savedAvatarData = new Map();
      for (const m of preLoadModels) {
        if (m.type === 'plexian_avatar') {
          savedAvatarData.set(m.id, { type: m.type, avatarConfig: m.avatarConfig, avatarId: m.avatarId });
        }
      }

      // If roomId changed, force a fresh load (ignore editing state)
      const roomIdChanged = lastLoadedRoomId.current !== null && lastLoadedRoomId.current !== roomId;

      // Mark new room ID in store FIRST to prevent stale localStorage hydration
      usePlacedModelsStore.getState().setRoomId(roomId);

      if (roomIdChanged) {
        console.log('🔄 Room ID changed, forcing fresh load:', roomId);
        // Reset editing state when switching rooms
        isUserEditing.current = false;
        lastUserInteraction.current = 0;
        // Clear stores immediately to prevent old room flash
        isInitializing.current = true;
        usePlacedModelsStore.setState({ placedModels: [] });
        // Reset room store - will be hydrated from localStorage but that's OK since we'll load from backend
        // DO NOT clear localStorage as it breaks persistence and causes infinite loops
        useRoomStore.setState(createDefaultRoomConfig());
        // Reset flag after a brief delay
        setTimeout(() => {
          isInitializing.current = false;
        }, 50);
      } else if (lastLoadedRoomId.current === null) {
        // Check if we just navigated from another step editing this same room.
        // The EditorPage remounts on each route change (step1→step2→step3), resetting
        // all useRef values. The module-level globals survive those remounts.
        if (_globalEditingRoomId === roomId) {
          console.log('⚡ Step navigation detected - skipping backend reload, local store is source of truth');
          lastLoadedRoomId.current = roomId;
          roomDataReady.current = true;
          // Restore room metadata that was lost when the component remounted
          if (_globalRoomName) setRoomName(_globalRoomName);
          if (_globalRoomSlug) setRoomMeta({ slug: _globalRoomSlug });
          setRoomTags(_globalRoomTags);
          setListedInDirectory(_globalListedInDirectory);
          setIsLoading(false);
          return;
        }

        // First load for this room - clear stores to prevent showing stale cached data
        console.log('🆕 First load - clearing cached data for new room:', roomId);
        isInitializing.current = true;
        usePlacedModelsStore.setState({ placedModels: [] });
        // Reset room store - backend data will be the source of truth
        // DO NOT clear localStorage as it breaks persistence and causes infinite loops
        useRoomStore.setState(createDefaultRoomConfig());
        // Reset flag after a brief delay
        setTimeout(() => {
          isInitializing.current = false;
        }, 50);
      } else {
        // Same room already loaded this session — local state is always source of truth.
        // The auto-save keeps the backend in sync; reloading from backend here would
        // overwrite freshly uploaded videos/decorations that haven't saved yet.
        console.log('⚠️ Skipping backend room load - same room already loaded this session');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        roomDataReady.current = false; // Prevent auto-save until data is loaded

        // Initialize API connection for asset loading with error handling
        try {
          await useApiStore.getState().checkConnection();
        } catch (apiError) {
          console.warn('API connection failed, continuing with offline mode:', apiError);
        }

        // Initialize asset loader with error handling
        try {
          const assetLoaderSuccess = await assetLoader.initialize();
          if (!assetLoaderSuccess) {
            console.warn('Asset loader initialization failed, continuing with limited functionality');
          }
        } catch (assetError) {
          console.warn('Asset loader error, continuing with limited functionality:', assetError);
        }

        // Load room data with proper error handling
        let roomData = null;
        try {
          roomData = await roomApi.getRoom(roomId, { isSignedIn, user });

          // Track room edit for existing rooms (room existed in backend)
          track(Events.ROOM_EDITED_EXISTING, { roomId, slug: roomData.slug || null });

          // Store room metadata for slug-based navigation
          if (roomData.slug) {
            setRoomMeta({ slug: roomData.slug });
            _globalRoomSlug = roomData.slug;
          }

          // Store room name
          if (roomData.name) {
            setRoomName(roomData.name);
            _globalRoomName = roomData.name;
          }
          setRoomTags(roomData.tags || []);
          _globalRoomTags = roomData.tags || [];
          setListedInDirectory(roomData.listedInDirectory !== false);
          _globalListedInDirectory = roomData.listedInDirectory !== false;
          setHasPassword(!!roomData.hasPassword);
          setIsPublished(!!(roomData.publishedId || roomData.publishedAt));

          // Store creator username for door navigation
          if (roomData.creatorUsername) {
            useRoomStore.getState().setCreatorUsername(roomData.creatorUsername);
          }

          // Store guest room expiration time (if present)
          if (roomData.expiresAt) {
            setRoomExpiresAt(roomData.expiresAt);
          }

          // Check ownership - redirect to player mode if not the owner
          if (roomData.isOwner === false) {
            console.log(`User does not own room ${roomId}, redirecting to player mode`);
            // Use slug URL if available
            if (roomData.slug) {
              navigate(ROUTES.ROOM_BY_SLUG(roomData.slug));
            } else {
              navigate(ROUTES.PLAYER(roomId));
            }
            return;
          }

          // Check if screenshot needs re-capture due to version mismatch
          const roomScreenshotVersion = roomData.screenshotVersion || 0;
          const hasExistingScreenshot = !!roomData.screenshotS3Key;
          if (hasExistingScreenshot && roomScreenshotVersion < SCREENSHOT_VERSION) {
            console.log(`Screenshot version mismatch (room: ${roomScreenshotVersion}, current: ${SCREENSHOT_VERSION}) - will re-capture`);
            const decorations = roomData.roomConfig?.decorations || [];
            setTimeout(() => {
              triggerAutoScreenshotCapture(roomId, user, decorations);
            }, 1000);
          }
        } catch (roomError) {
          if (roomError.response?.status === 404) {
            console.log(`Room ${roomId} not found - creating new room`);
            // Create default room data for new rooms
            roomData = {
              isOwner: true,
              roomConfig: createDefaultRoomConfig(),
              placedModels: []
            };
          } else if (roomError.response?.status === 403) {
            // Access denied - redirect to player or home
            console.log(`Access denied to room ${roomId}`);
            setError('You do not have permission to edit this room.');
            setTimeout(() => navigate(ROUTES.HOME), 2000);
            return;
          } else {
            throw roomError; // Re-throw non-404 errors
          }
        }

        // DON'T clear store here - we'll replace data directly to avoid flashing
        // Clearing and re-adding causes models to flash in/out

        // Update stores with loaded data - use DIRECT setState to avoid re-renders
        if (roomData?.roomConfig) {
          try {
            const loadedConfig = roomData.roomConfig;

            // Reconstruct public S3 URLs from assetKey — fixes rooms saved with
            // expired presigned URLs in materialOptions.src or decoration.option
            const s3Base = process.env.REACT_APP_S3_BASE_URL;
            const rawMaterialOptions = loadedConfig.materialOptions || {};
            const materialOptions = rawMaterialOptions.assetKey
              ? { ...rawMaterialOptions, src: `${s3Base}/${rawMaterialOptions.assetKey}` }
              : rawMaterialOptions;
            const decorations = (loadedConfig.decorations || []).map(d =>
              d.type === 'video' && d.assetKey
                ? { ...d, option: `${s3Base}/${d.assetKey}` }
                : d
            );

            // Use a single setState call to update all room config at once
            useRoomStore.setState({
              scaleX: loadedConfig.scaleX || DEFAULT_ROOM_CONFIG.scaleX,
              scaleZ: loadedConfig.scaleZ || DEFAULT_ROOM_CONFIG.scaleZ,
              height: loadedConfig.height || DEFAULT_ROOM_CONFIG.height,
              materialType: loadedConfig.materialType || DEFAULT_ROOM_CONFIG.materialType,
              materialId: loadedConfig.materialId || DEFAULT_ROOM_CONFIG.materialId,
              materialOptions,
              decorations,
              lightIntensity: loadedConfig.lightIntensity ?? DEFAULT_ROOM_CONFIG.lightIntensity,
              roomLiningVisible: loadedConfig.roomLiningVisible ?? DEFAULT_ROOM_CONFIG.roomLiningVisible,
              shaderOptions: loadedConfig.shaderOptions || {}
            });
          } catch (configError) {
            console.error('Failed to update room configuration:', configError);
            // Use defaults if config update fails
          }
        }

        // Directly set the placedModels array instead of adding one by one
        // This prevents flashing and avoids triggering history for each model
        if (roomData?.placedModels && Array.isArray(roomData.placedModels)) {
          try {
            const validModels = roomData.placedModels
              .filter((model, index) => {
                if (!model || typeof model !== 'object') {
                  console.warn(`Skipping invalid model at index ${index}:`, model);
                  return false;
                }
                return true;
              })
              .map((model, index) => {
                const defaults = applyModelDefaults(model, index);
                // Restore avatar config from localStorage snapshot — the backend
                // persists type/avatarId in metadata but NOT the full config blob
                if (defaults.type === 'plexian_avatar' && !defaults.avatarConfig && savedAvatarData.has(defaults.id)) {
                  const local = savedAvatarData.get(defaults.id);
                  defaults.avatarConfig = local.avatarConfig;
                  defaults.avatarId = local.avatarId || defaults.avatarId;
                }
                return defaults;
              });

            // Batch-prefetch ALL presigned URLs in a single API call before models render.
            // This populates the presignedUrlCache so individual GLBObject components
            // find URLs already cached and don't make concurrent individual API calls
            // (which fail ~50% of the time under load).
            const assetIds = validModels
              .map(m => m.assetId)
              .filter(id => id && typeof id === 'string');
            if (assetIds.length > 0) {
              try {
                await assetsApi.getBatchPresignedUrls(assetIds);
                console.log(`✅ Batch-prefetched presigned URLs for ${assetIds.length} models`);
              } catch (err) {
                console.warn('Batch presigned URL prefetch failed, models will fetch individually:', err);
              }
            }

            // Batch-prefetch avatar configs for any placed avatars that only have an avatarId
            const avatarIds = validModels
              .filter(m => m.type === 'plexian_avatar' && m.avatarId && !m.avatarConfig)
              .map(m => m.avatarId);
            if (avatarIds.length > 0) {
              try {
                const { avatarApi } = require('../services/avatarApi');
                const avatarConfigs = await avatarApi.getAvatarsByIds(avatarIds);
                for (const model of validModels) {
                  if (model.avatarId && avatarConfigs[model.avatarId]) {
                    model.avatarConfig = avatarConfigs[model.avatarId].config;
                  }
                }
                console.log(`Batch-prefetched avatar configs for ${avatarIds.length} avatars`);
              } catch (err) {
                console.warn('Batch avatar config prefetch failed, avatars will fetch individually:', err);
              }
            }

            // Single setState to replace all models at once - NO FLASHING
            // Update room ID in store to match loaded models
            usePlacedModelsStore.setState({ placedModels: validModels, _roomId: roomId });
            console.log(`✅ Loaded ${validModels.length} models in single operation`);
          } catch (modelsError) {
            console.error('Failed to process placed models:', modelsError);
            // Set empty array on error
            usePlacedModelsStore.setState({ placedModels: [], _roomId: roomId });
          }
        } else {
          // No models to load, set empty array
          usePlacedModelsStore.setState({ placedModels: [], _roomId: roomId });
        }

        // Set editor mode with error handling
        try {
          useUserInterfaceStore.getState().setPlayerMode("editor");
        } catch (uiError) {
          console.error('Failed to set editor mode:', uiError);
        }

        // Update last loaded room ID
        lastLoadedRoomId.current = roomId;
        roomDataReady.current = true; // Now safe to auto-save

        console.log('✅ Room loaded successfully');

      } catch (err) {
        console.error("Failed to load room:", err);

        // Determine if this is a recoverable error
        const isRecoverable = err.response?.status === 404 ||
                             err.name === 'NetworkError' ||
                             err.message?.includes('CORS') ||
                             err.message?.includes('fetch');

        if (isRecoverable && retryCount < 2) {
          // For recoverable errors, initialize with defaults and suggest retry
          console.log('Initializing with default room due to recoverable error');
          try {
            usePlacedModelsStore.getState().clearAll();
            useRoomStore.getState().resetRoom();
            useUserInterfaceStore.getState().setPlayerMode("editor");
          } catch (initError) {
            console.error('Failed to initialize default room:', initError);
          }

          setError(`Unable to load saved room data. ${err.message}. You can continue with a blank room or retry.`);
        } else {
          // For unrecoverable errors or after max retries
          setError(`Failed to load room: ${err.message}. Please try refreshing the page.`);
        }
      } finally {
        setIsLoading(false);
      }
    };

    // Catch any synchronous errors in loadRoom setup
    try {
      loadRoom();
    } catch (syncError) {
      console.error('Synchronous error in loadRoom:', syncError);
      handleAsyncError(syncError, { context: 'loadRoom setup' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, isLoadingRoomId, isMigrating, retryCount, navigate]); // isMigrating re-triggers load once migration completes

  // Detect changes after publishing
  // useEffect(() => {
  //   if (publishStatus === 'published' && lastPublishedData) {
  //     const currentData = JSON.stringify({ roomConfig, placedModels });
  //     setHasUnpublishedChanges(currentData !== lastPublishedData);
  //   }
  // }, [roomConfig, placedModels, publishStatus, lastPublishedData]);

  // Auto-save room data when it changes (debounced)
  const lastSavedData = useRef(null);
  const isInitialLoad = useRef(true);
  const lastUserInteraction = useRef(0); // Start at 0, not Date.now()!
  const isUserEditing = useRef(false);
  const [syncStatus, setSyncStatus] = useState('synced'); // 'synced', 'pending', 'syncing', 'error'

  // Store roomId and user info in refs to avoid stale closures in debounced function
  const roomIdRef = useRef(roomId);
  roomIdRef.current = roomId; // Update on each render

  const userAuthRef = useRef({ isSignedIn, user });
  userAuthRef.current = { isSignedIn, user }; // Update on each render

  // Create debounced auto-save function once and keep it stable
  // Using useCallback with memoization to prevent unnecessary debounce recreation
  const debouncedAutoSave = useRef(null);

  // Wire global flush reference so video upload components can trigger an immediate save
  _globalFlushSave = () => debouncedAutoSave.current?.flush();

  // Initialize debounce function once on mount
  if (!debouncedAutoSave.current) {
    debouncedAutoSave.current = debounce(async (currentRoomConfig, currentPlacedModels) => {
      // Note: We use refs instead of function arguments to avoid stale closure issues
      if (!roomIdRef.current) {
        console.log('⚠️ Auto-save skipped: No roomId available');
        return;
      }

      // CRITICAL: Don't save if room data hasn't been loaded yet - prevents overwriting with empty data
      if (!roomDataReady.current) {
        console.log('⚠️ Auto-save skipped: Room data not ready yet');
        return;
      }

      try {
        // Mark that user is actively editing to prevent API overwrites
        isUserEditing.current = true;
        lastUserInteraction.current = Date.now();
        setSyncStatus('syncing');

        const roomData = { roomConfig: currentRoomConfig, placedModels: currentPlacedModels };
      console.log('🔄 Auto-saving room data to backend (1.5s debounced)...');
        console.log('DEBUG: Auto-save auth state:', {
          currentIsSignedIn: userAuthRef.current.isSignedIn,
          currentUserExists: !!userAuthRef.current.user,
          currentUserId: userAuthRef.current.user?.id,
          hasCurrentUser: !!userAuthRef.current.user
        });

        // Create userInfo object for authenticated saves
        const userInfo = userAuthRef.current.isSignedIn && userAuthRef.current.user ? {
          isSignedIn: true,
          user: {
            id: userAuthRef.current.user.id
          }
        } : null;

        console.log('DEBUG: userInfo being passed to saveRoom:', userInfo);

        // Save to backend — await so flush() returns a real Promise (used by togglePlayerMode)
        try {
          await roomApi.saveRoom(roomIdRef.current, roomData, userInfo);
          console.log('✅ Room synced to backend successfully');
          lastSavedData.current = JSON.stringify(roomData);
          setSyncStatus('synced');
          
          // Auto-capture screenshot after successful save
          triggerAutoScreenshotCapture(roomIdRef.current, userAuthRef.current.user, decorations);
        } catch (saveError) {
          // Only log unexpected auto-save errors
          if (saveError.response?.status !== 404) {
            console.error("Backend sync failed:", saveError.message);
            setSyncStatus('error');
          } else {
            // 404s during auto-save are normal for the create-on-save flow
            setSyncStatus('synced');
          }
        }

        // Keep editing state for longer to prevent race conditions
        setTimeout(() => {
          isUserEditing.current = false;
        }, 3000); // Keep editing state for 3 seconds after save starts
      } catch (error) {
        // Reset editing state on error
        isUserEditing.current = false;
        setSyncStatus('error');
        console.error("Auto-save setup failed:", error.message);
      }
    }, 1500); // 1.5 second delay - balances responsiveness with request efficiency
  }

  // Trigger auto-save when room data changes
  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }

    // CRITICAL: Don't auto-save while room is loading - prevents overwriting real data with empty defaults
    if (isLoading || isInitializing.current) {
      console.log('⏳ Skipping auto-save - room is still loading');
      return;
    }

    const currentData = JSON.stringify({ roomConfig, placedModels });
    if (currentData !== lastSavedData.current) {
      // Update user interaction timestamp when data changes
      lastUserInteraction.current = Date.now();
      isUserEditing.current = true;
      // Stamp module-level globals so the next mounted step knows not to reload
      _globalEditingRoomId = roomIdRef.current;
      setSyncStatus('pending');

      console.log('🎯 Local state updated - will sync to backend in 1.5s (UI already updated)');
      // Pass room data to debounced function - uses userAuthRef for auth info
      debouncedAutoSave.current(roomConfig, placedModels);
    }
  }, [roomConfig, placedModels, isLoading]);

  // Flush auto-save on unmount and navigation
  useEffect(() => {
    return () => {
      // Flush any pending auto-saves before unmounting
      if (debouncedAutoSave.current) {
        debouncedAutoSave.current.flush();
      }
      _globalFlushSave = null;
    };
  }, []);

  // Also flush on page unload (e.g., refresh, close tab)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (debouncedAutoSave.current) {
        debouncedAutoSave.current.flush();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Ensure room is saved when leaving this page
  useEffect(() => {
    return () => {
      // On unmount/navigation, make sure any pending saves are flushed
      if (debouncedAutoSave.current) {
        console.log('📄 Page unmounting - flushing auto-save...');
        debouncedAutoSave.current.flush();
      }
    };
  }, []);

  // Add visibility change listener to save when tab regains focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page regained focus - flush any pending saves to ensure persistence
        if (debouncedAutoSave.current && syncStatus === 'pending') {
          console.log('🔄 Page regained focus - flushing pending auto-save...');
          debouncedAutoSave.current.flush();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [syncStatus]);

  const togglePlayerMode = async () => {
    useUserInterfaceStore.getState().setEditingRoom(false);

    // Await save flush before switching modes — ensures backend has latest video URL
    // before PlayerPage fetches room data (critical on slow mobile connections)
    if (debouncedAutoSave.current) {
      console.log('⚡ Awaiting auto-save flush before mode switch...');
      try {
        await debouncedAutoSave.current.flush();
      } catch (e) {
        console.warn('Save before preview failed, continuing anyway:', e);
      }
    }

    const currentMode = useUserInterfaceStore.getState().playerMode;

    if (currentMode === "editor") {
      // Switching to player mode - navigate to player page
      setIsNavigatingToPreview(true);
      try {
        const userInfo = isSignedIn ? { isSignedIn, user } : null;
        const playerUrl = await getPlayerUrl(roomId, userInfo);
        navigate(playerUrl);
      } catch (error) {
        console.error('Failed to navigate to player:', error);
        // Fallback to direct player URL
        navigate(ROUTES.PLAYER(roomId));
      } finally {
        setIsNavigatingToPreview(false);
      }
    } else {
      // Switching back to editor mode - just toggle mode
      useUserInterfaceStore.getState().setPlayerMode("editor");
    }
  };

  // Handle screenshot capture (currently unused while manual capture UI is commented out)
  // eslint-disable-next-line no-unused-vars
  const _handleCaptureScreenshot = async () => {
    const renderer = getEditorRenderer();
    if (!renderer) {
      setScreenshotError('Renderer not available. Please try again.');
      return;
    }

    if (!roomId) {
      setScreenshotError('Room not loaded yet.');
      return;
    }

    setIsCapturingScreenshot(true);
    setScreenshotError(null);

    // Store original state for restoration
    const wasEditingRoom = useUserInterfaceStore.getState().editingRoom;
    const originalCameraPosition = cameraControls ? {
      x: cameraControls.camera.position.x,
      y: cameraControls.camera.position.y,
      z: cameraControls.camera.position.z
    } : null;

    try {
      // Exit editing mode to show wall decorations
      useUserInterfaceStore.getState().setEditingRoom(false);

      // Move camera to a wider view for screenshot (position much further out for wide angle)
      if (cameraControls) {
        const roomHeight = useRoomStore.getState().height || 50;
        const distance = Math.max(roomHeight * 4, 300);
        cameraControls.setTarget(0, roomHeight / 2, 0, true);
        cameraControls.setPosition(distance, distance, distance, true);
        // Wait for camera animation to complete
        await new Promise(resolve => setTimeout(resolve, 750));
      }

      const userInfo = isSignedIn && user ? { isSignedIn: true, user: { id: user.id } } : null;
      await captureAndSaveRoomThumbnail(renderer, roomId, userInfo, { decorations });
      console.log('Screenshot captured successfully');
    } catch (error) {
      console.error('Screenshot capture failed:', error);
      setScreenshotError(error.message || 'Failed to capture screenshot');
    } finally {
      // Restore editing mode
      useUserInterfaceStore.getState().setEditingRoom(wasEditingRoom);

      // Restore camera position
      if (cameraControls && originalCameraPosition) {
        cameraControls.setTarget(0, 0, 0, true);
        cameraControls.setPosition(originalCameraPosition.x, originalCameraPosition.y, originalCameraPosition.z, true);
      }

      setIsCapturingScreenshot(false);
    }
  };

  // Get current player mode for button text
  const currentPlayerMode = useUserInterfaceStore((s) => s.playerMode);

  const rotateLeft = () => {
    cameraControls?.rotate(Math.PI / 2, 0, true);
  };

  const rotateRight = () => {
    cameraControls?.rotate(-Math.PI / 2, 0, true);
  };

  // Handle publish button click
  const handlePublishClick = async () => {
    if (isSignedIn) {
      await handlePublishRoom();
    } else {
      // User not authenticated
      if (isMobile) {
        // CRITICAL: Preserve session ID before Clerk redirect
        // This ensures guest rooms can be migrated after sign-up
        const preserveSession = useSessionStore.getState().preserveSessionForAuth;
        preserveSession();
        useSessionStore.getState().setPendingAuthRedirect(ROUTES.EDITOR.SETTINGS(roomId));
        console.log('[AUTH] Session preserved before sign-up redirect from EditorPage');

        // On mobile, redirect directly to Clerk's signup (skips intermediate page)
        redirectToSignUp({
          redirectUrl: ROUTES.EDITOR.SETTINGS(roomId),
          afterSignUpUrl: ROUTES.EDITOR.SETTINGS(roomId)
        });
      } else {
        // On desktop, go to signup page with room preview
        useSessionStore.getState().setPendingAuthRedirect(ROUTES.EDITOR.SETTINGS(roomId));
        navigate(ROUTES.SIGNUP(roomId));
      }
    }
  };

  // Handle actual publishing
  const handlePublishRoom = async () => {
    if (!isSignedIn || !user) {
      alert("You must be signed in to publish a room.");
      return;
    }

    if (isPublishing) {
      return; // Prevent double publishing
    }

    setIsPublishing(true);

    try {
      // Prepare room data for publishing
      const roomData = {
        roomConfig,
        placedModels,
        metadata: {
          title: "My Room", // Default title
          description: "",
          createdBy: user.id,
          createdAt: new Date().toISOString(),
        },
      };

      console.log("Publishing room with data:", roomData);

      // Get JWT session token
      const token = await getToken();
      if (!token) {
        alert("Unable to get authentication token. Please sign in again.");
        return;
      }

      // Call publishing API
      const result = await publishingApi.publishRoom(roomId, roomData, token);

      if (result.success) {
        // Use slug URL if available, otherwise fall back to published URL
        const shareUrl = roomMeta.slug
          ? `${window.location.origin}${ROUTES.ROOM_BY_SLUG(roomMeta.slug)}`
          : result.data.publishedUrl;
        setPublishedUrl(shareUrl);
        setIsPublished(true);
        setShowPublishSuccess(true);
        track(Events.ROOM_PUBLISHED, {
          roomId,
          slug: roomMeta.slug || null,
          modelCount: placedModels?.length ?? 0,
        });
        console.log("Room published successfully:", result.data);
        
        // Auto-capture screenshot after successful publish
        triggerAutoScreenshotCapture(roomId, user, decorations);
      } else {
        // Handle publishing errors
        track(Events.PUBLISH_FAILED, {
          roomId,
          slug: roomMeta.slug || null,
          errorCode: result.error.code,
          errorMessage: result.error.message,
          editorStep: keyForPanel,
        });
        if (
          result.error.code === "PUBLISHING_BLOCKED" &&
          result.error.requiresReview
        ) {
          alert(
            "Publishing Blocked\nYour room contains content that requires manual review. This is normal for certain types of content."
          );
        } else {
          alert(
            `Publishing Failed\n${
              result.error.message ||
              "An error occurred while publishing your room."
            }`
          );
        }
        console.error("Publishing failed:", result.error);
      }
    } catch (error) {
      console.error("Unexpected publishing error:", error);
      track(Events.PUBLISH_FAILED, {
        roomId,
        slug: roomMeta.slug || null,
        errorCode: 'UNEXPECTED_ERROR',
        errorMessage: error.message,
        editorStep: keyForPanel,
      });
      alert(
        `Publishing Failed\n${
          error.message ||
          "An unexpected error occurred while publishing your room."
        }`
      );
    } finally {
      setIsPublishing(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Box
        w="100vw"
        h="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="gray.100"
      >
        <Box textAlign="center">
          <Spinner size="xl" mb={4} />
          <Text>Loading room data...</Text>
        </Box>
      </Box>
    );
  }

  // Error state with recovery options
  if (error) {
    return (
      <Box
        w="100vw"
        h="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="gray.100"
        p={4}
      >
        <AlertRoot status="error" maxW="lg">
          <AlertContent>
            <Box>
              <Text fontWeight="bold" mb={2}>Room Loading Error</Text>
              <Text mb={4} color="gray.600">{error}</Text>

              <HStack spacing={3}>
                <Button
                  colorScheme="blue"
                  onClick={() => {
                    setError(null);
                    setRetryCount(prev => prev + 1);
                  }}
                  disabled={retryCount >= 2}
                >
                  {retryCount >= 2 ? 'Max Retries Reached' : `Retry ${retryCount > 0 ? `(${retryCount + 1}/3)` : ''}`}
                </Button>

                <Button
                  variant="outline"
                  onClick={() => {
                    // Clear error and continue with default room
                    setError(null);
                    try {
                      usePlacedModelsStore.getState().clearAll();
                      useRoomStore.getState().resetRoom();
                      useUserInterfaceStore.getState().setPlayerMode("editor");
                      setIsLoading(false);
                    } catch (err) {
                      console.error('Failed to initialize blank room:', err);
                      navigate(ROUTES.HOME);
                    }
                  }}
                >
                  Continue with Blank Room
                </Button>

                <Button variant="ghost" onClick={() => navigate(ROUTES.HOME)}>
                  Go Home
                </Button>
              </HStack>
            </Box>
          </AlertContent>
        </AlertRoot>
      </Box>
    );
  }

  // Green primary/navigation buttons (Back / Next Step / Publish).
  const navBtnStyle = {
    height: "40px",
    px: "8px",
    py: "8px",
    justifyContent: "center",
    alignItems: "center",
    gap: "4px",
    bg: "#26EF96",
    boxShadow: "2px 2px 0 0 #FFF",
    borderRadius: "0",
    cursor: "pointer",
    _hover: { opacity: 0.9 },
    transition: "opacity 0.2s",
  };
  const navBtnTextStyle = {
    color: "black",
    fontFamily: "DM Mono",
    fontWeight: "500",
    lineHeight: "normal",
    textAlign: "center",
  };
  // Minimalist utility buttons (Home / Preview / Tutorial / toolbar) — white
  // box, black outline, black DM Mono text, matching the modal aesthetic.
  const utilBtnStyle = {
    bg: "white",
    color: "black",
    border: "2px solid #000",
    borderRadius: "0",
    fontFamily: "DM Mono",
    fontWeight: "500",
    _hover: { bg: "gray.100" },
  };

  return (
    <>
    {/* Black backdrop so Safari iOS samples black for top/bottom bars */}
    <div style={{ position: 'fixed', inset: 0, background: '#000', zIndex: -1 }} />
    <Grid
      templateColumns={
        currentPlayerMode === "editor"
          ? { base: "1fr", md: "1fr 350px" }
          : "1fr"
      }
      templateRows={
        currentPlayerMode === "editor"
          ? { base: "50vh auto", md: "1fr" }
          : "1fr"
      }
      w="100vw"
      h="100vh"
      position="fixed"
      top={0}
      left={0}
      overflow="hidden"
    >
      {/* 3D Canvas */}
      <GridItem
        bg="gray.200"
        overflow="hidden"
        position="relative"
        minH={{ base: "50vh", md: "100%" }}
      >
        {/* Arrow Buttons for room editing - only show in editor mode */}
        {editingRoom && currentPlayerMode === "editor" && (
          <>
            <Box
              as="button"
              aria-label="Rotate Left"
              onClick={rotateLeft}
              position="absolute"
              top="50%"
              left="20px"
              transform="translateY(-50%)"
              zIndex="1000"
              display="flex"
              alignItems="center"
              justifyContent="center"
              w={{ base: "40px", md: "48px" }}
              h={{ base: "64px", md: "72px" }}
              bg="white"
              border="2px solid #000"
              borderRadius="0"
              color="black"
              cursor="pointer"
              _hover={{ bg: "gray.100" }}
              transition="background 0.15s"
            >
              <Icon boxSize={{ base: "7", md: "9" }} strokeWidth="1">
                <VscChevronLeft />
              </Icon>
            </Box>
            <Box
              as="button"
              aria-label="Rotate Right"
              onClick={rotateRight}
              position="absolute"
              top="50%"
              right="20px"
              transform="translateY(-50%)"
              zIndex="1000"
              display="flex"
              alignItems="center"
              justifyContent="center"
              w={{ base: "40px", md: "48px" }}
              h={{ base: "64px", md: "72px" }}
              bg="white"
              border="2px solid #000"
              borderRadius="0"
              color="black"
              cursor="pointer"
              _hover={{ bg: "gray.100" }}
              transition="background 0.15s"
            >
              <Icon boxSize={{ base: "7", md: "9" }} strokeWidth="1">
                <VscChevronRight />
              </Icon>
            </Box>
          </>
        )}

        <MainScene />
        {/* Step Indicator - commented out for now */}
        {/* {currentPlayerMode === "editor" && (
          <StepIndicator currentStep={keyForPanel} />
        )} */}
        {/* Pointer Lock UI for player mode */}
        {currentPlayerMode === "player" && <PointerLockUI />}
        {/* Desktop transform controls: below home button, top-left */}
        {!isMobile && currentPlayerMode === "editor" && (
          <Box position="fixed" top="54px" left="10px" zIndex={20}>
            <TransformTools />
          </Box>
        )}
        {/* Desktop move panel: bottom-left, shown when toggled */}
        {!isMobile && currentPlayerMode === "editor" && showDesktopMovePanel && (
          <Box
            position="fixed"
            bottom={{ base: "auto", md: "60px" }}
            top={{ base: "calc(50vh - 280px)", md: "auto" }}
            left="10px"
            zIndex={20}
            overflow="hidden"
            transform={{ base: "scale(0.5)", md: "none" }}
            transformOrigin={{ base: "bottom left", md: "top left" }}
          >
            <MobileTransformPanel />
          </Box>
        )}
        <delTunnel.Out></delTunnel.Out>
        <uploadImageTunnel.Out></uploadImageTunnel.Out>
        <videoErrorTunnel.Out></videoErrorTunnel.Out>
        <publishSuccessTunnel.Out></publishSuccessTunnel.Out>
        {showPublishSuccess && publishedUrl && (
          <PublishSuccessModal
            publishedUrl={publishedUrl}
            roomId={roomId}
            onExit={() => navigate(ROUTES.PROFILE)}
            onClose={() => setShowPublishSuccess(false)}
          />
        )}
      </GridItem>

      {/* Sidebar - only show in editor mode */}
      {currentPlayerMode === "editor" && (
        <GridItem
          w={{ base: "100%", md: "350px" }}
          h={{ base: "auto", md: "100%" }}
          bg="gray.800"
          color="white"
          overflowY="auto"
          overflowX="hidden"
        >
          {/* Mobile step-3 transform mode: toggle button frozen above the transform panel */}
          {isMobile && keyForPanel === "step3" && mobileEditMode === "transform" && (
            <Box position="sticky" top="0" zIndex={5} bg="gray.800" px={3} pt={3} pb={1}>
              {renderMobileModeToggle()}
            </Box>
          )}

          {/* Show transform panel or asset panel based on mobile edit mode.
              In assets mode the toggle is injected below the sticky "MODELS"
              title so the label sits on top and the button freezes beneath it. */}
          {isMobile && keyForPanel === "step3" && mobileEditMode === "transform" ? (
            <MobileTransformPanel />
          ) : (
            <ObjectPanel
              key={keyForPanel}
              panels={panels}
              headerExtra={
                isMobile && keyForPanel === "step3" ? (
                  <Box pt={3}>{renderMobileModeToggle()}</Box>
                ) : undefined
              }
              componentProps={keyForPanel === "step4" ? {
                roomId,
                roomName,
                roomTags,
                listedInDirectory,
                hasPassword,
                onRoomNameChange: (newName, newSlug) => {
                  setRoomName(newName);
                  _globalRoomName = newName;
                  if (newSlug) {
                    setRoomMeta({ slug: newSlug });
                    _globalRoomSlug = newSlug;
                  }
                },
                onTagsChange: (newTags) => { setRoomTags(newTags); _globalRoomTags = newTags; },
                onListedInDirectoryChange: (val) => { setListedInDirectory(val); _globalListedInDirectory = val; },
              } : {}}
            />
          )}
        </GridItem>
      )}

      {/* Toolbar Panel and Tutorial Button - only show in editor mode */}
      {currentPlayerMode === "editor" && (
        <Box
          position="fixed"
          top={{ base: "calc(50vh - 50px)", md: "auto" }}
          bottom={{ base: "auto", md: "10px" }}
          left="10px"
          zIndex="1000"
          display="flex"
          gap="2"
          alignItems="flex-end"
        >
          <ToolbarPanel />
          <Box>
            <TutorialButton onClick={() => setShowTutorial(true)} />
          </Box>
        </Box>
      )}

      {/* Bottom-Right Navigation Buttons */}
      {currentPlayerMode === "editor" && (
        <HStack
          position="fixed"
          top={{ base: "calc(50vh - 50px)", md: "auto" }}
          bottom={{ base: "auto", md: "10px" }}
          right={{ base: "10px", md: "360px" }}
          zIndex="1000"
          spacing={2}
        >
          {/* Back button - step 2b goes back to step 2 */}
          {keyForPanel === "step2b" && (
            <Box
              onClick={() => navigate(ROUTES.EDITOR.WALLS(roomId))}
              display="flex"
              width={{ base: "60px", md: "70px" }}
              {...navBtnStyle}
            >
              <Text
                fontSize={{ base: "12px", md: "14px" }}
                {...navBtnTextStyle}
              >
                Back
              </Text>
            </Box>
          )}

          {/* Back button - step 3 goes back to step 2b */}
          {keyForPanel === "step3" && (
            <Box
              onClick={() => navigate(ROUTES.EDITOR.DECORATIONS(roomId))}
              display="flex"
              width={{ base: "60px", md: "70px" }}
              {...navBtnStyle}
            >
              <Text
                fontSize={{ base: "12px", md: "14px" }}
                {...navBtnTextStyle}
              >
                Back
              </Text>
            </Box>
          )}

          {/* Next Step button - step 1 goes to step 2 (wall type) */}
          {keyForPanel === "step1" && (
            <Box
              onClick={() => navigate(ROUTES.EDITOR.WALLS(roomId))}
              display="flex"
              width={{ base: "100px", md: "120px" }}
              {...navBtnStyle}
            >
              <Text
                fontSize={{ base: "12px", md: "14px" }}
                {...navBtnTextStyle}
              >
                Next Step
              </Text>
            </Box>
          )}

          {/* Next Step button - step 2 goes to step 2b (decorations) */}
          {keyForPanel === "step2" && (
            <Box
              onClick={() => navigate(ROUTES.EDITOR.DECORATIONS(roomId))}
              display="flex"
              width={{ base: "100px", md: "120px" }}
              {...navBtnStyle}
            >
              <Text
                fontSize={{ base: "12px", md: "14px" }}
                {...navBtnTextStyle}
              >
                Next Step
              </Text>
            </Box>
          )}

          {/* Next Step button - step 2b goes to step 3 */}
          {keyForPanel === "step2b" && (
            <Box
              onClick={() => {
                useUserInterfaceStore.getState().setEditingRoom(false);
                resetCameraControls(cameraControls, lockCameraForEditor);
                // Use slug URL if available
                if (roomMeta.slug) {
                  navigate(ROUTES.EDITOR.ROOT(roomMeta.slug));
                } else {
                  navigate(ROUTES.EDITOR.ROOT(roomId));
                }
              }}
              display="flex"
              width={{ base: "100px", md: "120px" }}
              {...navBtnStyle}
            >
              <Text
                fontSize={{ base: "12px", md: "14px" }}
                {...navBtnTextStyle}
              >
                Next Step
              </Text>
            </Box>
          )}

          {/* Next Step button - step 3 goes to step 4 (room settings) */}
          {keyForPanel === "step3" && (
            <Box
              onClick={() => navigate(ROUTES.EDITOR.SETTINGS(roomId))}
              display="flex"
              width={{ base: "100px", md: "120px" }}
              {...navBtnStyle}
            >
              <Text
                fontSize={{ base: "12px", md: "14px" }}
                {...navBtnTextStyle}
              >
                Next Step
              </Text>
            </Box>
          )}

          {/* Back button - step 4 goes back to step 3 */}
          {keyForPanel === "step4" && (
            <Box
              onClick={() => {
                // Use slug URL if available
                if (roomMeta.slug) {
                  navigate(ROUTES.EDITOR.ROOT(roomMeta.slug));
                } else {
                  navigate(ROUTES.EDITOR.ROOT(roomId));
                }
              }}
              display="flex"
              width={{ base: "60px", md: "70px" }}
              {...navBtnStyle}
            >
              <Text
                fontSize={{ base: "12px", md: "14px" }}
                {...navBtnTextStyle}
              >
                Back
              </Text>
            </Box>
          )}

          {/* Publish Room button - only on step 4 */}
          {keyForPanel === "step4" && (
            <Box
              onClick={handlePublishClick}
              display="flex"
              width={{ base: "110px", md: "130px" }}
              {...navBtnStyle}
              opacity={isPublishing ? 0.6 : 1}
              pointerEvents={isPublishing ? "none" : "auto"}
            >
              <Text
                fontSize={{ base: "12px", md: "14px" }}
                {...navBtnTextStyle}
              >
                {isPublishing ? "Publishing..." : "Publish Room"}
              </Text>
            </Box>
          )}
        </HStack>
      )}

      {/* Top Controls */}
      <HStack
        position="absolute"
        top={{ base: "calc(env(safe-area-inset-top) + 24px)", md: "10px" }}
        left="10px"
        zIndex="10000"
        spacing={2}
      >
        <Button
          size="sm"
          onClick={() => (isPublished ? navigate(ROUTES.HOME) : setShowExitConfirm(true))}
          {...utilBtnStyle}
        >
          <Icon as={VscHome} />
          Home
        </Button>

        <Button
          size="sm"
          onClick={togglePlayerMode}
          {...utilBtnStyle}
          isLoading={isNavigatingToPreview}
          loadingText="Loading..."
        >
          <Icon as={currentPlayerMode === "editor" ? VscEye : VscEdit} />
          {currentPlayerMode === "editor" ? "Preview Player" : "Back to Editor"}
        </Button>

        {/* <Button
          size="sm"
          onClick={handleCaptureScreenshot}
          bg="black"
          color="white"
          _hover={{ bg: "gray.800" }}
          borderRadius="0"
          isLoading={isCapturingScreenshot}
          loadingText="Capturing..."
          disabled={!roomId || isCapturingScreenshot}
          title="Capture room screenshot for thumbnail"
        >
          <Icon as={VscDeviceCamera} />
          Capture Screenshot
        </Button> */}

        {/* Commenting out old publish button for now */}
        {/* {publishStatus === 'published' && !hasUnpublishedChanges ? (
          <Button size="sm" onClick={handleSharePublished} colorScheme="green">
            <Icon as={VscShare} />
            Share
          </Button>
        ) : publishStatus === 'published' && hasUnpublishedChanges ? (
          <Button
            size="sm"
            onClick={handleRepublishRoom}
            colorScheme={publishStatus === 'publishing' ? 'yellow' : 'orange'}
            isLoading={publishStatus === 'publishing'}
            loadingText="Re-publishing..."
          >
            <Icon as={VscCloudUpload} />
            {publishStatus === 'publishing' ? 'Re-publishing...' : 'Re-publish'}
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={handlePublishRoom}
            colorScheme={publishStatus === 'publishing' ? 'yellow' : 'blue'}
            isLoading={publishStatus === 'publishing'}
            loadingText="Publishing..."
          >
            <Icon as={VscCloudUpload} />
            {publishStatus === 'publishing' ? 'Publishing...' : 'Publish'}
          </Button>
        )} */}
      </HStack>

      {/* Sync Status Indicator - Top Right (before user profile) */}
      {currentPlayerMode === "editor" && (
        <Box
          position="absolute"
          top={{ base: "calc(env(safe-area-inset-top) + 24px)", md: "10px" }}
          right={isSignedIn ? "60px" : "10px"}
          zIndex="10000"
          bg="blackAlpha.700"
          px={3}
          py={1}
          borderRadius="0"
          fontSize="xs"
          fontFamily="DM Mono"
          fontWeight="500"
          color={syncStatus === 'error' ? 'red.300' : syncStatus === 'synced' ? 'green.300' : 'yellow.300'}
          display="flex"
          alignItems="center"
          gap={1}
        >
          {syncStatus === 'syncing' && <Spinner size="xs" />}
          {syncStatus === 'pending' && '⏱️'}
          {syncStatus === 'synced' && '✓'}
          {syncStatus === 'error' && '⚠️'}
          <Text>
            {syncStatus === 'syncing' ? 'Saving...' :
             syncStatus === 'pending' ? 'Pending' :
             syncStatus === 'synced' ? 'Saved' :
             'Save Failed'}
          </Text>
        </Box>
      )}

      {/* User Profile - Top Right */}
      {isSignedIn && (
        <Box position="absolute" top={{ base: "calc(env(safe-area-inset-top) + 24px)", md: "10px" }} right="10px" zIndex="10000">
          <UserButton
            afterSignOutUrl="/"
            appearance={{
              elements: {
                avatarBox: "w-8 h-8",
              },
            }}
          />
        </Box>
      )}

      {/* Room ID Display */}
      {/* <Box
        position="absolute"
        top="10px"
        right="10px"
        zIndex="10000"
        bg="blackAlpha.700"
        px={2}
        py={1}
        borderRadius="md"
        color="white"
        fontSize="xs"
      >
        Room: {roomId}
      </Box> */}

      {/* Player Mode Instructions */}
      {currentPlayerMode === "player" && !isMobile && (
        <Box
          position="absolute"
          bottom="20px"
          left="50%"
          transform="translateX(-50%)"
          zIndex="10000"
          bg="blackAlpha.700"
          px={4}
          py={2}
          borderRadius="0"
          color="white"
          fontSize="sm"
          fontFamily="DM Mono"
          fontWeight="500"
          textAlign="center"
        >
          <Text>Use WASD to move • Mouse to look • Click to lock pointer</Text>
        </Box>
      )}
      {/* Tutorial Overlay - only show in editor mode */}
      {currentPlayerMode === "editor" && showTutorial && (
        <TutorialOverlay
          currentStep={keyForPanel}
          isVisible={showTutorial}
          onComplete={() => {
            localStorage.setItem(`tutorial_completed_${keyForPanel}`, "true");
            setShowTutorial(false);
          }}
          onSkip={() => {
            localStorage.setItem(`tutorial_completed_${keyForPanel}`, "true");
            setShowTutorial(false);
          }}
        />
      )}

      {/* Guest Session Warning - show for guest users editing rooms with TTL */}
      {!isSignedIn && roomExpiresAt && currentPlayerMode === "editor" && (
        <GuestSessionWarning
          roomId={roomId}
          expiresAt={roomExpiresAt}
        />
      )}

    </Grid>
    {showExitConfirm && (
      <ExitEditorModal
        onConfirm={() => navigate(ROUTES.HOME)}
        onCancel={() => setShowExitConfirm(false)}
      />
    )}
    </>
  );
}

// Main EditorPage component wrapped with ErrorBoundary
function EditorPage(props) {
  const navigate = useNavigate();

  const handleErrorBoundaryRetry = () => {
    // Force a re-render by updating key
    window.location.reload();
  };

  const handleErrorBoundaryReset = () => {
    navigate(ROUTES.HOME);
  };

  return (
    <ErrorBoundary
      onRetry={handleErrorBoundaryRetry}
      onReset={handleErrorBoundaryReset}
      title="Editor Loading Error"
      message="The room editor encountered a critical error. This might be due to corrupted room data, network issues, or browser compatibility problems."
    >
      <EditorPageContent {...props} />
    </ErrorBoundary>
  );
}

export default EditorPage;
